import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import {
   getIspServers,
   getIspRouterProfiles,
   getIspRouterAddressing,
   getIspPlans,
   type IspRouterProfile,
} from '@/api/isp';
import {
   toRateLimit,
   fromRateLimit,
   cleanNumber,
   profileNameFor,
   categoryOptions,
} from './ispPlanFields';
import {
   Stepper,
   Bullet,
   Connector,
   StepCaption,
   NavRow,
   NavBtns,
} from './ispWizardUI';
import {
   SettingsField,
   SettingsLbl,
   SettingsInput,
   SettingsHintSm,
} from './ispSettingsUI';
import type { IspPlanRow, IspPlanDraft } from '@/api/isp';

/**
 * An internet plan: what the customer pays, and the RouterOS profile that
 * actually enforces it.
 *
 * The rate-limit fields follow RouterOS's own shape and are stored as typed
 * ("50M/50M"), because RouterOS accepts those strings directly and re-deriving
 * them from numbers is how unit bugs get in.
 *
 * GRACE IS PER PLAN (Ray, 2026-08-28). A business package and a ₱799 home line
 * do not get the same patience, and an operator who sets one number for
 * everybody ends up cutting off the customer they least wanted to.
 *
 * ─── 🔴 THE PROFILE IS PICKED, NOT TYPED ───
 * Ray spotted the free-text profile name as wrong, 2026-08-28. A typo there is
 * SILENT: the plan saves, the account is created, and the customer gets whatever
 * the router's default profile allows instead of the speed they pay for.
 * Nothing in SmartFi would disagree with itself, because nothing asked the
 * router. So the list comes off the box.
 *
 * ─── EITHER ORDER WORKS ───
 * Ray, 2026-08-31: *"we have to considere the user actions that either which
 * created first like internet plans or server our system should be flexible."*
 * Operators write their price list before registering a MikroTik at least as
 * often as the other way round. So a plan SAVES with no server and no profile,
 * says plainly what is still missing, and can be linked later from either end.
 * Refusing to save would be the product inventing an order it has no business
 * demanding.
 *
 * Three states, and the form is usable in all of them:
 *   no servers registered → say so, save the plan anyway
 *   server picked, reachable → real profiles in a dropdown
 *   server picked, unreachable → say why, fall back to typing it
 */
export type PlanDraft = IspPlanDraft;

interface Props {
   existing?: IspPlanRow | null;
   onCancel: () => void;
   onSave: (draft: PlanDraft) => void;
}

const IspPlanForm: React.FC<Props> = ({ existing, onCancel, onSave }) => {
   /**
    * A half-filled plan survives a reload.
    *
    * Ray, 2026-08-31: *"the form fillup should be persistent so a page realod is
    * safe."* This form has fourteen fields and three of them need the router
    * answered first; losing it to a stray refresh is a genuinely bad five
    * minutes. Keyed per plan so editing one does not resurrect a draft of
    * another. Cleared on save and on cancel — a kept draft the operator already
    * discarded is worse than none.
    */
   const draftKey = `smartfi:isp-plan-draft:${existing?.id ?? 'new'}`;

   const blank = {
      name: existing?.name ?? '',
      price: String(existing?.price ?? ''),
      speedLabel: existing?.speedLabel ?? '',
      servers: existing?.servers ?? ([] as string[]),
      routerProfile: existing?.routerProfile ?? '',
      bwUp: fromRateLimit(existing?.bandwidth ?? '').up,
      bwDown: fromRateLimit(existing?.bandwidth ?? '').down,
      burstUp: fromRateLimit(existing?.burstMax ?? '').up,
      burstDown: fromRateLimit(existing?.burstMax ?? '').down,
      thrUp: fromRateLimit(existing?.burstThreshold ?? '').up,
      thrDown: fromRateLimit(existing?.burstThreshold ?? '').down,
      burstSeconds: String(existing?.burstSeconds ?? 8),
      localAddress: existing?.localAddress ?? '',
      remoteAddress: existing?.remoteAddress ?? '',
      graceDays: String(existing?.graceDays ?? 3),
      category: existing?.category ?? '',
      remarks: existing?.remarks ?? '',
   };

   type Draft = typeof blank;
   const [f, setF] = useState<Draft>(() => {
      try {
         const raw = window.localStorage.getItem(draftKey);
         // Merged over `blank`, never used raw: a draft saved before a field
         // existed would otherwise leave that field undefined and the input
         // uncontrolled.
         return raw ? ({ ...blank, ...JSON.parse(raw) } as Draft) : blank;
      } catch {
         return blank;
      }
   });

   useEffect(() => {
      try {
         window.localStorage.setItem(draftKey, JSON.stringify(f));
      } catch {
         // A full or blocked localStorage must never stop somebody saving a plan.
      }
   }, [draftKey, f]);

   const clearDraft = () => {
      try {
         window.localStorage.removeItem(draftKey);
      } catch {
         /* nothing to clean up */
      }
   };
   /**
    * Two steps, because one screen was taller than the window.
    *
    * Ray, 2026-08-31: *"make this form 2 step wizard to avoid the screen height
    * too long."* The split is by AUDIENCE, not by counting fields in half: step
    * one is the product an operator is selling (name, price, who it is for,
    * which routers), step two is the RouterOS enforcement they mostly want
    * defaulted. Somebody adding a price can stop after step one and take the
    * suggestions.
    */
   const [step, setStep] = useState<1 | 2>(1);
   const [addingCategory, setAddingCategory] = useState(false);
   const [errors, setErrors] = useState<Record<string, boolean>>({});
   const [message, setMessage] = useState('');

   const { data: servers } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
      staleTime: 30_000,
   });
   // Only a router SmartFi can actually read is worth offering: picking one it
   // cannot reach would give an empty dropdown and no explanation.
   const serverList = servers ?? [];

   const [profiles, setProfiles] = useState<IspRouterProfile[]>([]);
   const [profileReason, setProfileReason] = useState('');
   const [loadingProfiles, setLoadingProfiles] = useState(false);
   const [pools, setPools] = useState<string[]>([]);
   const [localAddrs, setLocalAddrs] = useState<string[]>([]);

   // Categories the operator has already used, so a price list does not end up
   // holding "Residential", "residential" and "Resi".
   const { data: allPlans = [] } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
      staleTime: 30_000,
   });
   const categories = categoryOptions(
      allPlans.map((p) => p.category),
      f.category
   );

   /** The profile SmartFi will create, named after the plan. */
   const suggestedProfile = profileNameFor(f.name);

   // Read the chosen router's /ppp/profile list. Re-runs when the operator
   // switches server, because profiles belong to ONE box.
   /**
    * Read profiles and addressing off the FIRST chosen router.
    *
    * One is enough: a plan on several MikroTiks gets the same profile name and
    * the same rate on each, and asking every box would only produce a list the
    * operator has to reconcile. The write goes to all of them.
    */
   const firstServer = f.servers[0] ?? '';
   useEffect(() => {
      if (!firstServer) {
         setProfiles([]);
         setProfileReason('');
         setPools([]);
         setLocalAddrs([]);
         return;
      }
      let alive = true;
      setLoadingProfiles(true);
      Promise.all([
         getIspRouterProfiles(firstServer),
         getIspRouterAddressing(firstServer),
      ])
         .then(([pr, ad]) => {
            if (!alive) return;
            setProfiles(pr.profiles);
            setProfileReason(pr.reason);
            setPools(ad.pools);
            setLocalAddrs(ad.localAddresses);
            // Fill the two addresses the operator cannot be expected to know,
            // but never overwrite something they already chose.
            setF((old) => ({
               ...old,
               remoteAddress: old.remoteAddress || ad.suggestedPool,
               localAddress: old.localAddress || ad.suggestedLocal,
            }));
         })
         .catch(() => {
            if (alive) setProfileReason('Could not reach that router just now.');
         })
         .finally(() => {
            if (alive) setLoadingProfiles(false);
         });
      return () => {
         alive = false;
      };
   }, [firstServer]);

   const set = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
   ) => setF((old) => ({ ...old, [e.target.name]: e.target.value }));

   /** A plan can run on several MikroTiks; each is toggled on or off. */
   const toggleServer = (id: string) =>
      setF((old) => ({
         ...old,
         servers: old.servers.includes(id)
            ? old.servers.filter((x: string) => x !== id)
            : [...old.servers, id],
      }));

   /** Rate fields take digits only — the unit is added on save. */
   const setNum = (e: React.ChangeEvent<HTMLInputElement>) =>
      setF((old) => ({ ...old, [e.target.name]: cleanNumber(e.target.value) }));

   const submit = () => {
      const next: Record<string, boolean> = {};
      if (!f.name.trim()) next.name = true;
      const price = Number(f.price);
      if (!Number.isFinite(price) || price < 0) next.price = true;
      const grace = Number(f.graceDays);
      if (!Number.isFinite(grace) || grace < 0 || grace > 60) next.graceDays = true;
      const burst = Number(f.burstSeconds);
      if (!Number.isFinite(burst) || burst < 0) next.burstSeconds = true;

      setErrors(next);
      if (Object.keys(next).length) {
         setMessage('Check the highlighted fields.');
         return;
      }
      setMessage('');
      clearDraft();
      onSave({
         name: f.name.trim(),
         price: Math.round(price),
         speedLabel: f.speedLabel.trim(),
         servers: f.servers,
         // Falls back to the derived name so a plan can never be saved with no
         // profile at all — that was the state that made "Create on router"
         // impossible and left the operator with nothing to pick.
         routerProfile: f.routerProfile.trim() || suggestedProfile,
         bandwidth: toRateLimit(f.bwUp, f.bwDown),
         burstMax: toRateLimit(f.burstUp, f.burstDown),
         burstThreshold: toRateLimit(f.thrUp, f.thrDown),
         burstSeconds: Math.trunc(burst),
         localAddress: f.localAddress.trim(),
         remoteAddress: f.remoteAddress.trim(),
         graceDays: Math.trunc(grace),
         category: f.category.trim(),
         remarks: f.remarks.trim(),
      });
   };

   return (
      <Wrapper>
         <Stepper>
            <Bullet $active={step === 1} $done={step > 1}>
               {step > 1 ? '✓' : '1'}
            </Bullet>
            <Connector $done={step > 1} />
            <Bullet $active={step === 2} $done={false}>
               2
            </Bullet>
         </Stepper>
         <StepCaption>
            Step {step} of 2 —{' '}
            <strong>{step === 1 ? 'The plan' : 'How the router enforces it'}</strong>
         </StepCaption>

         <Grid style={{ display: step === 1 ? 'grid' : 'none' }}>
            <SettingsField>
               <SettingsLbl>Plan name</SettingsLbl>
               <Field name="name" value={f.name} placeholder="e.g. Home 50" $err={!!errors.name} onChange={set} />
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Price (₱ per month)</SettingsLbl>
               <Field name="price" type="number" min={0} value={f.price} placeholder="1299" $err={!!errors.price} onChange={set} />
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Speed shown to customers</SettingsLbl>
               <Field name="speedLabel" value={f.speedLabel} placeholder="50 Mbps" onChange={set} />
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Category</SettingsLbl>
               <Sel
                  name="category"
                  value={addingCategory ? '__new' : f.category}
                  onChange={(e) => {
                     if (e.target.value === '__new') {
                        setAddingCategory(true);
                        setF((old) => ({ ...old, category: '' }));
                        return;
                     }
                     setAddingCategory(false);
                     setF((old) => ({ ...old, category: e.target.value }));
                  }}
               >
                  <option value="">No category</option>
                  {categories.map((c) => (
                     <option key={c} value={c}>
                        {c}
                     </option>
                  ))}
                  <option value="__new">+ Add a new category…</option>
               </Sel>
               {addingCategory && (
                  <Field
                     name="category"
                     value={f.category}
                     placeholder="e.g. Barangay promo"
                     onChange={set}
                     autoFocus
                  />
               )}
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Grace period (days)</SettingsLbl>
               <Field name="graceDays" type="number" min={0} max={60} value={f.graceDays} placeholder="3" $err={!!errors.graceDays} onChange={set} />
               <SettingsHintSm>
                  Days past due before an account on this plan is cut off.
               </SettingsHintSm>
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Runs on which MikroTiks</SettingsLbl>
               {serverList.length === 0 ? (
                  <NoneNote>No MikroTik registered yet</NoneNote>
               ) : (
                  <CheckList>
                     {serverList.map((sv) => (
                        <CheckRow key={sv.id}>
                           <input
                              type="checkbox"
                              checked={f.servers.includes(sv.id)}
                              onChange={() => toggleServer(sv.id)}
                           />
                           <span>
                              {sv.name}
                              {sv.setupStage === 'ready' ? '' : ' — not reporting'}
                           </span>
                        </CheckRow>
                     ))}
                  </CheckList>
               )}
               <SettingsHintSm>
                  {serverList.length
                     ? 'Tick every router that sells this plan. SmartFi writes the same profile to each.'
                     : 'You can save the plan now and link it to a router later — either order is fine.'}
               </SettingsHintSm>
            </SettingsField>
            <SettingsField>
               <SettingsLbl>RouterOS profile</SettingsLbl>
               {/* SmartFi CREATES this profile, so SmartFi names it. The
                   dropdown's only other job is adopting a profile the operator
                   already has — which is why there is no empty option: "not
                   chosen" was a state nobody could resolve. */}
               <Sel name="routerProfile" value={f.routerProfile || suggestedProfile} onChange={set}>
                  <option value={suggestedProfile}>
                     {suggestedProfile
                        ? `${suggestedProfile} — SmartFi will create this`
                        : 'Name the plan first'}
                  </option>
                  {profiles
                     .filter((pr) => pr.name !== suggestedProfile)
                     .map((pr) => (
                        <option key={pr.id} value={pr.name}>
                           Use existing: {pr.name}
                           {pr.rateLimit ? ` (${pr.rateLimit})` : ''}
                        </option>
                     ))}
               </Sel>
               <SettingsHintSm>
                  {!firstServer
                     ? 'SmartFi creates this profile on the router when you press “Create on router”.'
                     : loadingProfiles
                       ? 'Reading what is already on that router…'
                       : profileReason
                         ? `Could not read that router — ${profileReason} SmartFi will still create its own profile.`
                         : profiles.length
                           ? 'Or adopt one already on the router, read off it just now.'
                           : 'That router has no PPPoE profiles yet — SmartFi will create this one.'}
               </SettingsHintSm>
            </SettingsField>

         </Grid>

         {/* Kept MOUNTED, not unmounted, so switching steps cannot lose a
             half-typed value or re-trigger the router reads. */}
         <Grid style={{ display: step === 2 ? 'grid' : 'none' }}>
            {/* Plain numbers in megabits. The operator never types "M" and
                never types a slash — the two things that let a rate be saved in
                a shape RouterOS silently ignores. */}
            <SettingsField>
               <SettingsLbl>Speed (Mbps)</SettingsLbl>
               <PairRow>
                  <Unit>
                     <Field name="bwDown" value={f.bwDown} placeholder="50" onChange={setNum} inputMode="decimal" />
                     <UnitTag>down</UnitTag>
                  </Unit>
                  <Unit>
                     <Field name="bwUp" value={f.bwUp} placeholder="50" onChange={setNum} inputMode="decimal" />
                     <UnitTag>up</UnitTag>
                  </Unit>
               </PairRow>
               <SettingsHintSm>What the customer actually gets, in Mbps.</SettingsHintSm>
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Burst up to (Mbps)</SettingsLbl>
               <PairRow>
                  <Unit>
                     <Field name="burstDown" value={f.burstDown} placeholder="60" onChange={setNum} inputMode="decimal" />
                     <UnitTag>down</UnitTag>
                  </Unit>
                  <Unit>
                     <Field name="burstUp" value={f.burstUp} placeholder="60" onChange={setNum} inputMode="decimal" />
                     <UnitTag>up</UnitTag>
                  </Unit>
               </PairRow>
               <SettingsHintSm>Optional. A short boost above the plan speed.</SettingsHintSm>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Burst starts below (Mbps)</SettingsLbl>
               <PairRow>
                  <Unit>
                     <Field name="thrDown" value={f.thrDown} placeholder="40" onChange={setNum} inputMode="decimal" />
                     <UnitTag>down</UnitTag>
                  </Unit>
                  <Unit>
                     <Field name="thrUp" value={f.thrUp} placeholder="40" onChange={setNum} inputMode="decimal" />
                     <UnitTag>up</UnitTag>
                  </Unit>
               </PairRow>
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Burst time (seconds)</SettingsLbl>
               <Field name="burstSeconds" type="number" min={0} value={f.burstSeconds} placeholder="8" $err={!!errors.burstSeconds} onChange={set} />
            </SettingsField>

            {/* Both read off the router. The operator has no way to invent
                these, and asking them to was the bug. */}
            <SettingsField>
               <SettingsLbl>Gateway on the router</SettingsLbl>
               <Sel name="localAddress" value={f.localAddress} onChange={set}>
                  <option value="">Let the router decide</option>
                  {localAddrs.map((a) => (
                     <option key={a} value={a}>
                        {a}
                     </option>
                  ))}
               </Sel>
               <SettingsHintSm>
                  The address subscribers see as their gateway. Read off this
                  router&apos;s own profiles — leave it alone if you are unsure.
               </SettingsHintSm>
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Customer IPs come from</SettingsLbl>
               <Sel name="remoteAddress" value={f.remoteAddress} onChange={set}>
                  <option value="">Let the router decide</option>
                  {pools.map((a) => (
                     <option key={a} value={a}>
                        {a}
                     </option>
                  ))}
               </Sel>
               <SettingsHintSm>
                  {pools.length
                     ? 'An IP pool that already exists on this router.'
                     : 'Pick a MikroTik above and SmartFi will list its IP pools.'}
               </SettingsHintSm>
            </SettingsField>

            <SettingsField $wide>
               <SettingsLbl>Remarks (optional)</SettingsLbl>
               <Field name="remarks" value={f.remarks} placeholder="Anything worth noting about this plan" onChange={set} />
            </SettingsField>
         </Grid>

         {message && <Err>{message}</Err>}

         <NavRow>
            <Ghost
               type="button"
               onClick={() => {
                  // A draft the operator has deliberately abandoned must not
                  // come back the next time they open the form.
                  clearDraft();
                  onCancel();
               }}
            >
               Cancel
            </Ghost>
            <NavBtns>
               {step === 2 && (
                  <Ghost type="button" onClick={() => setStep(1)}>
                     Back
                  </Ghost>
               )}
               {step === 1 ? (
                  <Primary type="button" onClick={() => setStep(2)}>
                     Next
                  </Primary>
               ) : (
                  <Primary type="button" onClick={submit}>
                     {existing ? 'Save changes' : 'Add plan'}
                  </Primary>
               )}
            </NavBtns>
         </NavRow>
      </Wrapper>
   );
};

export default IspPlanForm;

const Wrapper = styled.div`
   width: 680px;
   max-width: 100%;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px 16px;
   margin-top: 4px;

   @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Divider = styled.div`
   grid-column: 1 / -1;
   margin-top: 6px;
   padding-top: 10px;
   border-top: 1px solid ${colors.GRAY13};
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
`;

const Field = styled(SettingsInput)<{ $err?: boolean }>`
   border-color: ${({ $err }) => ($err ? colors.DANGER : colors.GRAY12)};
`;

const Err = styled.p`
   margin: 10px 0 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 18px;
`;

const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;

/**
 * A picker, used wherever the honest answer is a list rather than a text box.
 *
 * The RouterOS profile was free text until 2026-08-28. A typo there is silent —
 * the plan saves and the customer quietly gets the wrong speed.
 */
const Sel = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   width: 100%;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

/** Two numbers side by side — down and up, in that order, as operators say it. */
const PairRow = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 8px;
`;

const Unit = styled.div`
   position: relative;
   display: flex;
   align-items: center;
`;

/** The unit lives in the field, so it can never be typed into the value. */
const UnitTag = styled.span`
   position: absolute;
   right: 10px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   pointer-events: none;
`;

const CheckList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   max-height: 132px;
   overflow-y: auto;
`;

const CheckRow = styled.label`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   cursor: pointer;
`;

const NoneNote = styled.div`
   padding: 8px 10px;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 6px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
